import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Toast, showApolloErrorToUser } from '@pletly/ui';

import { LIMITS } from '@pletly/shared';
import { useLoadingOverlay } from '%context/LoadingOverlay';
import { usePremium } from '%context/Premium';
import { GetProtegesDocument } from '%context/Connections/__generated__/getProteges.generated';
import { GetUserInfoDocument } from '%components/Profile/__generated__/userInfo.generated';
import { useAcceptInvitationMutation } from './__generated__/acceptInvite.generated';
import {
	GetInvitations_currentUser_friendshipRequestsReceived,
	GetInvitations_currentUser_friendshipRequestsSent,
} from './__generated__/GetInvitations';
import { GetUserGuardians, GetUserGuardiansVariables } from './__generated__/GetUserGuardians';
import { GET_USER_CONNECTIONS_COUNT } from './userConnections.graphql';
import { GetInvitationsDocument } from './__generated__/getInvitations.generated';

export const INVITES_QUERIES_TO_REFETCH = [GetInvitationsDocument, GetProtegesDocument];

export const useAcceptInvitation = () => {
	const { navigate } = useNavigation();
	const { type: premiumType, status: premiumStatus } = usePremium();
	const [loadUserConnections] = useLazyQuery<GetUserGuardians, GetUserGuardiansVariables>(
		GET_USER_CONNECTIONS_COUNT,
		{
			fetchPolicy: 'network-only',
		},
	);
	const [accept] = useAcceptInvitationMutation({
		refetchQueries: INVITES_QUERIES_TO_REFETCH,
	});

	const { showLoadingOverlay, closeLoadingOverlay } = useLoadingOverlay();
	const acceptInvite = useCallback(
		async (
			invite:
				| (GetInvitations_currentUser_friendshipRequestsSent & { fromUser: never })
				| (GetInvitations_currentUser_friendshipRequestsReceived & { toUser: never }),
		) => {
			showLoadingOverlay();
			try {
				if (!invite.toUser) {
					await accept({
						variables: {
							id: invite.id,
						},
						refetchQueries: [
							...INVITES_QUERIES_TO_REFETCH,
							{ query: GetUserInfoDocument, variables: { id: invite.fromUser.id } },
						],
					});
				}

				const connections = await loadUserConnections({
					variables: {
						id: invite.toUser.id,
					},
				});

				const connectionsCount = connections.data?.user.guardians.length ?? 0;
				const canAccept =
					premiumStatus === 'feature_disabled' ||
					connections.data?.user.role === 'PAL' ||
					connectionsCount < LIMITS.maxConnectionsPerCareReceiver[premiumType];

				if (!canAccept) {
					return navigate('Paywall');
				}

				await accept({
					variables: {
						id: invite.id,
					},
					refetchQueries: INVITES_QUERIES_TO_REFETCH,
				});

				Toast.show({
					type: 'success',
					text1: 'Invite accepted',
				});
			} catch (error) {
				showApolloErrorToUser(error as Error);
			} finally {
				closeLoadingOverlay();
			}
		},
		[
			loadUserConnections,
			premiumType,
			premiumStatus,
			accept,
			navigate,
			showLoadingOverlay,
			closeLoadingOverlay,
		],
	);

	return acceptInvite;
};
